import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReloadButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Button, Stack, Tooltip } from 'src/core/ui';
import { useHasWasteSaleFeature } from 'src/hooks/features/useHasWasteSaleFeature';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanSetPrice } from 'src/hooks/useUserHasListingPermissions';
import { CreateReachProConversationModal } from 'src/modals/CreateReachProConversationModal';
import { LaunchWasteUnsoldTickets } from 'src/modals/ListingDetails/actions/LaunchWasteUnsoldTickets';
import { ModalFooter } from 'src/modals/Modal/Modal.styled';
import { IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getListingActionButtons,
  getListingDetailsModalConfigWithDeepLink,
} from 'src/utils/inventoryUtils';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import {
  Feature,
  ListingActionType,
  ListingDetailDataField,
  ListingDetails,
} from 'src/WebApiController';

import { LaunchCreateDonationSale } from './actions/LaunchCreateDonationSale';
import { LaunchDeleteSeatSaver } from './actions/LaunchDeleteSeatSaver';
import { LaunchViewComps } from './actions/LaunchViewComps';

export const ListingDetailsFooter = ({
  onSubmit,
  disabled,
  hasSelectedSections,
  setIsLoading,
}: {
  onSubmit?: () => void;
  disabled?: boolean;
  hasSelectedSections?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const hasWasteSaleFeature = useHasWasteSaleFeature();
  const hasAutoPricingEmptySelectedSectionsNoCompsFeature = useUserHasFeature(
    Feature.AutoPricingEmptySelectedSectionsNoComps
  );
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasDonateListingFeature = useUserHasFeature(Feature.DonateListing);

  const [isContactSupportModalOpen, setIsContactSupportModalOpen] =
    useState(false);

  const { closeModal } = useContext(ModalContext);
  const navigate = useNavigate();
  const isMobile = useMatchMedia('mobile');
  const {
    event,
    posEntity: listing,
    isLoading,
    errorInfo,
    setActivePosEntity,
  } = useActivePosEntityContext<ListingDetails>();
  const cancelTo = getListingDetailsModalConfigWithDeepLink(
    isMobile,
    listing?.id
  );

  const onReload = useCallback(() => {
    if (listing) {
      setActivePosEntity(
        listing.id,
        listing.idOnMkp,
        true,
        [ListingDetailDataField.Basic],
        true
      );
    }
  }, [listing, setActivePosEntity]);

  const canSetPrice = useUserCanSetPrice(listing, false);

  const navigateToWastedSale = useCallback(
    (saleId: number) => {
      navigate(getSaleDetailsRelativeUrl(saleId));
    },
    [navigate]
  );

  const { mainActions, subActions } = useMemo(() => {
    if (!listing) {
      return {};
    }

    const mainActions: { key: string; action: JSX.Element | null }[] = [
      {
        key: 'Refresh',
        action: (
          <ReloadButton
            disabled={disabled || isLoading}
            variant={'outline'}
            onClick={onReload}
          />
        ),
      },
    ];

    const uiActions: { key: string; action: JSX.Element | null }[] = [
      ...(canSetPrice
        ? [
            {
              key: 'ViewComps',
              action: (
                <LaunchViewComps
                  variant={'textPlain'}
                  listingId={listing.id}
                  marketplaceListingId={listing.idOnMkp}
                  cancelTo={cancelTo}
                  disabled={disabled || isLoading}
                />
              ),
            },
          ]
        : []),
    ];

    const actions: { key: string; action: JSX.Element | null }[] = [
      ...uiActions,
      ...listing.actions
        .filter(
          (a) =>
            a !== ListingActionType.FullySoldNoAction &&
            a !== ListingActionType.Waste &&
            a !== ListingActionType.Donate
        )
        .map((a, i) => ({
          key: a,
          action:
            getListingActionButtons(
              false /* for table */,
              listing,
              [a],
              event,
              a === ListingActionType.Broadcast ||
                a === ListingActionType.Unbroadcast
                ? 'regular'
                : a === ListingActionType.DownloadUserDocuments
                  ? 'outline'
                  : 'textPlain',
              false /* icon only mode */,
              cancelTo,
              disabled || isLoading,
              undefined /* lockListingGroupFromEdits */,
              undefined /* onSetListingActiveInGroup */
            )?.[0] ?? null,
        })),
    ];
    if (
      hasDonateListingFeature &&
      listing?.actions.includes(ListingActionType.Donate)
    ) {
      mainActions.push({
        key: ListingActionType.Donate,
        action: (
          <LaunchCreateDonationSale
            key={`${listing.id}_${ListingActionType.Donate}`}
            listingId={listing.id}
            marketplaceListingId={listing.idOnMkp}
            cancelTo={cancelTo}
            disabled={disabled}
            variant={'outline'}
          />
        ),
      });
    }
    const mainAction = actions.find((a) =>
      [
        ListingActionType.Broadcast,
        ListingActionType.Unbroadcast,
        ListingActionType.DownloadUserDocuments,
      ].includes(a.key as ListingActionType)
    );
    if (mainAction) {
      mainActions.push(mainAction);
    }

    if (onSubmit) {
      const shouldValidateSelectedSections =
        hasAutoPricingEmptySelectedSectionsNoCompsFeature &&
        !hasSelectedSections;

      const saveButton = (
        <OkButton
          onClick={onSubmit}
          textContentId={ContentId.Save}
          disabled={disabled || isLoading || shouldValidateSelectedSections}
        />
      );

      mainActions.push({
        key: 'Submit',
        action:
          shouldValidateSelectedSections && !disabled && !isLoading ? (
            <Tooltip
              triggerContent={
                <div style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                  {saveButton}
                </div>
              }
              align="center"
              delayDuration={0}
            >
              <Content id={ContentId.SelectAtLeastOneSection} />
            </Tooltip>
          ) : (
            saveButton
          ),
      });
    }

    const subActions = actions.filter(
      (a) =>
        ![
          ListingActionType.Broadcast,
          ListingActionType.Unbroadcast,
          ListingActionType.DownloadUserDocuments,
        ].includes(a.key as ListingActionType)
    );

    if (hasWasteSaleFeature) {
      const hasWasteListingAction = listing?.actions.includes(
        ListingActionType.Waste
      );
      if (hasWasteListingAction) {
        subActions.push({
          key: 'WasteTickets',
          action: (
            <LaunchWasteUnsoldTickets
              cancelTo={cancelTo}
              onWastedSuccess={navigateToWastedSale}
            />
          ),
        });
      }
    }
    return { mainActions, subActions };
  }, [
    canSetPrice,
    cancelTo,
    disabled,
    event,
    hasAutoPricingEmptySelectedSectionsNoCompsFeature,
    hasDonateListingFeature,
    hasSelectedSections,
    hasWasteSaleFeature,
    isLoading,
    listing,
    navigateToWastedSale,
    onReload,
    onSubmit,
  ]);

  if (errorInfo) {
    return <OkButton onClick={closeModal} />;
  }

  if (!listing) {
    return null;
  }

  return listing ? (
    <>
      <ModalFooter>
        <Stack
          gap="l"
          justifyContent="spaceBetween"
          alignItems="center"
          width="full"
        >
          <Stack gap="m" alignItems="center">
            {listing.isSeatSaver && (
              <LaunchDeleteSeatSaver
                disabled={disabled || isLoading}
                setIsLoading={setIsLoading}
                listingId={listing.id}
              />
            )}
            {hasReachProSupportFeature && (
              <Button
                variant="outline"
                onClick={() => setIsContactSupportModalOpen(true)}
                disabled={disabled || isLoading}
              >
                <Content id={ContentId.ContactSupport} />
              </Button>
            )}
          </Stack>
          <Stack gap="m" alignItems="center" style={{ marginLeft: 'auto' }}>
            {listing?.isDeleted ? (
              <OkButton onClick={closeModal} content={ContentId.Close} />
            ) : (
              <>
                {mainActions?.map((a) => (
                  <div key={a.key}>{a.action}</div>
                ))}
                {(subActions?.length ?? 0) > 0 ? (
                  <PosDropdown
                    trigger={
                      <MoreIcon
                        disabled={disabled || isLoading}
                        withHoverEffect
                        fill={IconsFill.textBrand}
                        align="middle"
                      />
                    }
                    triggerProps={{ disabled: disabled || isLoading }}
                    align="end"
                  >
                    {subActions!
                      .toSorted((a, b) => a.key.localeCompare(b.key))
                      .map((a) => (
                        <PosDropdownItem
                          disabled={disabled || isLoading}
                          key={a.key}
                        >
                          {a.action}
                        </PosDropdownItem>
                      ))}
                  </PosDropdown>
                ) : null}
              </>
            )}
          </Stack>
        </Stack>
      </ModalFooter>
      <CreateReachProConversationModal
        isOpen={isContactSupportModalOpen}
        onClose={() => setIsContactSupportModalOpen(false)}
        initialTopic="Issue with listing"
        initialEventId={listing?.viagEvId ?? undefined}
        initialListingId={
          listing?.mkp === 'StubHub' && listing?.idOnMkp
            ? parseInt(listing.idOnMkp)
            : undefined
        }
        initialSaleId={undefined}
        initialSection={listing?.seating?.section}
        initialRow={listing?.seating?.row ?? undefined}
        initialSeatFrom={listing?.seating?.seatFr ?? undefined}
        initialSeatTo={listing?.seating?.seatTo ?? undefined}
      />
    </>
  ) : null;
};
